import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../../../../../redux/hooks';
import ProgramUsers from '../ProgramUsers';
import ContentSettings from '../ContentSettings';
import MatchesMainPage from '../../../../Matches/MatchesMainPage';
import { SectionSelect } from './components/SettingsUI';
import { SettingsLayout } from './SettingsLayout';

export { GenericAccordion } from './components/SettingsUI';

function ProgramSettings() {
  const [searchParams] = useSearchParams();
  const sectionFromUrl = searchParams.get('section') === 'members' ? 'members' : 'settings';
  const tabFromUrl = searchParams.get('tab');
  const initialTab = tabFromUrl === 'design' || tabFromUrl === 'resources' ? tabFromUrl : 'configuration';
  const [activeTopTab, setActiveTopTab] = useState<
    'configuration' | 'resources' | 'design'
  >(initialTab);
  const [section, setSection] = useState<'settings' | 'members'>(sectionFromUrl);
  const [memberView, setMemberView] = useState<'members' | 'matches'>(
    sectionFromUrl === 'members' ? 'members' : 'members'
  );
  const [settingsRefetchKey, setSettingsRefetchKey] = useState(0);

  const groupData = useAppSelector((state) => state.groupDetails.data);
  const isDesignDisabled = groupData?.model === 'mentorship';
  const settingsView = activeTopTab === 'design' ? 'design' : 'configuration';
  const matchesDisabled = groupData?.model === 'masterclass';

  const memberViewOptions = useMemo(
    () => [
      { value: 'members', label: 'People', disabled: false },
      { value: 'matches', label: 'Matches', disabled: matchesDisabled }
    ],
    [matchesDisabled]
  );

  useEffect(() => {
    if (matchesDisabled && memberView === 'matches') {
      setMemberView('members');
    }
  }, [matchesDisabled, memberView]);

  useEffect(() => {
    if (isDesignDisabled && activeTopTab === 'design') {
      setActiveTopTab('configuration');
    }
  }, [activeTopTab, isDesignDisabled]);

  useEffect(() => {
    if (activeTopTab === 'configuration') {
      setSettingsRefetchKey((prev) => prev + 1);
    }
  }, [activeTopTab]);

  useEffect(() => {
    if (groupData?.model !== 'masterclass' && sectionFromUrl !== 'members') {
      setSection('members');
    }
  }, [groupData?.model, sectionFromUrl]);

  const renderTopTab = (
    tab: 'design' | 'configuration' | 'resources',
    label: string,
    disabled?: boolean
  ) => {
    const isSelected = activeTopTab === tab;
    return (
      <Box
        role="button"
        onClick={() => !disabled && setActiveTopTab(tab)}
        sx={{
          px: 2.5,
          py: 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          bgcolor: isSelected ? '#444444' : disabled ? '#151515' : '#2A2A2A',
          opacity: disabled ? 0.5 : 1,
          color: isSelected ? '#FDFDFD' : disabled ? '#868686' : '#B6B6B6',
          fontSize: 12,
          fontWeight: isSelected ? 700 : 400
        }}
      >
        {label}
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          bgcolor: '#0F0F0F',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            minHeight: 0
          }}
        >
          <Box
            sx={{
              flex: 1,
              bgcolor: '#1C1C1C',
              border: '1px solid #2A2A2A',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}
          >
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid #2A2A2A',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <SectionSelect
                section={section}
                onChange={(v) => setSection(v as 'settings' | 'members')}
              />

              {section === 'members' ? (
                <Box sx={{ ml: 'auto' }}>
                  <Box
                    sx={{
                      ml: 'auto',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 2.5
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#B6B6B6',
                        fontSize: 12,
                        fontWeight: 700,
                        fontFamily: 'Inter, sans-serif'
                      }}
                    >
                      View:
                    </Typography>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid #2A2A2A'
                      }}
                    >
                      {memberViewOptions.map((option) => {
                        const selected = memberView === option.value;
                        const disabled = option.disabled ?? false;
                        return (
                          <Box
                            key={option.value}
                            role="button"
                            sx={{
                              px: 1,
                              py: 0.5,
                              background: selected ? '#444444' : '#2A2A2A',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 2.5,
                              cursor: disabled ? 'not-allowed' : 'pointer',
                              color: selected ? '#FFFFFF' : '#B6B6B6',
                              fontSize: 12,
                              fontWeight: selected ? 700 : 400,
                              fontFamily: 'Inter, sans-serif',
                              opacity: disabled ? 0.5 : 1,
                              '&:not(:last-of-type)': {
                                borderRight: '1px solid #2A2A2A'
                              }
                            }}
                            onClick={() => {
                              if (disabled) return;
                              setMemberView(option.value as 'members' | 'matches');
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: selected ? 700 : 400,
                                color: selected ? '#FFFFFF' : '#B6B6B6'
                              }}
                            >
                              {option.label}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    ml: 'auto',
                    display: 'inline-flex',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  {renderTopTab('design', 'Design', isDesignDisabled)}
                  {renderTopTab('configuration', 'Configuration')}
                  {renderTopTab('resources', 'Resources')}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0
              }}
            >
              {section === 'members' ? (
                <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  {memberView === 'members' ? (
                    <ProgramUsers />
                  ) : (
                    <MatchesMainPage />
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {activeTopTab === 'resources' ? (
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                      <ContentSettings />
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                      <SettingsLayout
                        view={settingsView}
                        refetchKey={settingsRefetchKey}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default ProgramSettings;
